import { Fragment, useContext, useDeferredValue, useEffect, useMemo, useState } from 'react';
import { Combobox, Dialog, Transition } from '@headlessui/react';
import { MagnifyingGlassIcon, EyeIcon, UserIcon } from '@heroicons/react/24/outline';

import { AuthContext } from '../../utils/auth.js';
import { fetchAdminUsers } from '../../utils/adminApi.js';
import { buildUserIndex, searchUsers } from '../../utils/userSearch.js';
import { useImpersonation } from '../../context/ImpersonationContext.js';
import cn from '../../ui/cn.js';
import Kbd from '../../ui/Kbd.js';
import Avatar from '../../ui/Avatar.js';
import Spinner from '../../ui/Spinner.js';

function looksLikeUid(value = '') {
  return /^[A-Za-z0-9_-]{20,40}$/.test(value.trim());
}

/**
 * ViewAsDialog: admin picker to view the platform as another user.
 * Search by email, name or user id; paste a raw uid to use it directly.
 */
export default function ViewAsDialog() {
  const { currentUser } = useContext(AuthContext);
  const { pickerOpen, closePicker, start, canImpersonate, impersonated } = useImpersonation();
  const [query, setQuery] = useState('');
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!pickerOpen || !canImpersonate) return;
    let cancelled = false;
    setLoading(true);
    setError('');
    fetchAdminUsers(currentUser)
      .then((list) => {
        if (!cancelled) setUsers(list);
      })
      .catch(() => {
        if (!cancelled) setError('Could not load the user list. You can still paste a user id.');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [pickerOpen, canImpersonate, currentUser]);

  // Index once per list; the deferred query keeps typing responsive with thousands of users.
  const userIndex = useMemo(() => buildUserIndex(users), [users]);
  const deferredQuery = useDeferredValue(query);
  const results = useMemo(() => searchUsers(userIndex, deferredQuery, deferredQuery.trim() ? 20 : 12), [userIndex, deferredQuery]);

  const rawUid = looksLikeUid(query) && !results.some((u) => u.userId === query.trim()) ? query.trim() : null;

  const select = (item) => {
    if (!item) return;
    if (item.raw) start(item.userId, {});
    else start(item.userId, { email: item.email, name: item.displayName || item.name || '' });
    setQuery('');
  };

  if (!canImpersonate) return null;

  return (
    <Transition.Root show={pickerOpen} as={Fragment} afterLeave={() => setQuery('')}>
      <Dialog as="div" className="relative z-[80]" onClose={closePicker}>
        <Transition.Child as={Fragment} enter="ease-out duration-150" enterFrom="opacity-0" enterTo="opacity-100" leave="ease-in duration-100" leaveFrom="opacity-100" leaveTo="opacity-0">
          <div className="fixed inset-0 bg-gray-900/40 backdrop-blur-[2px]" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto p-4 sm:p-6 md:p-20">
          <Transition.Child as={Fragment} enter="ease-out duration-150" enterFrom="opacity-0 scale-[0.98]" enterTo="opacity-100 scale-100" leave="ease-in duration-100" leaveFrom="opacity-100 scale-100" leaveTo="opacity-0 scale-[0.98]">
            <Dialog.Panel className="mx-auto max-w-xl transform divide-y divide-gray-100 overflow-hidden rounded-2xl bg-white shadow-overlay ring-1 ring-gray-900/5 transition-all">
              <div className="flex items-center gap-3 px-4 py-3">
                <span className="inline-flex h-8 w-8 items-center justify-center rounded-lg bg-amber-50 text-amber-600">
                  <EyeIcon className="h-4 w-4" aria-hidden="true" />
                </span>
                <div className="min-w-0 flex-1">
                  <Dialog.Title className="text-sm font-semibold text-gray-900">View platform as a user</Dialog.Title>
                  <p className="text-xs text-gray-500">Read-only. Pick an account by email, name or id.</p>
                </div>
                {impersonated && <span className="truncate text-xs text-gray-400">Currently: {impersonated.email || impersonated.userId}</span>}
              </div>

              <Combobox onChange={select}>
                <div className="relative">
                  <MagnifyingGlassIcon className="pointer-events-none absolute left-4 top-3.5 h-5 w-5 text-gray-400" aria-hidden="true" />
                  <Combobox.Input
                    className="h-12 w-full border-0 bg-transparent pl-11 pr-4 text-gray-900 placeholder:text-gray-400 focus:ring-0 sm:text-sm"
                    placeholder="Search users or paste a user id…"
                    onChange={(event) => setQuery(event.target.value)}
                    autoComplete="off"
                  />
                  {loading && <Spinner className="absolute right-4 top-4 h-4 w-4 text-gray-400" />}
                </div>

                <Combobox.Options static className="max-h-[60vh] scroll-py-2 overflow-y-auto p-2">
                  {rawUid && (
                    <Combobox.Option value={{ userId: rawUid, raw: true }} className={({ active }) => cn('flex cursor-default select-none items-center gap-3 rounded-lg px-2.5 py-2', active ? 'bg-indigo-600 text-white' : 'text-gray-700')}>
                      {({ active }) => (
                        <>
                          <span className={cn('inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-md', active ? 'bg-white/15' : 'bg-gray-100 text-gray-500')}>
                            <UserIcon className="h-4 w-4" aria-hidden="true" />
                          </span>
                          <span className="min-w-0 flex-1">
                            <span className={cn('block truncate text-sm font-medium', active ? 'text-white' : 'text-gray-900')}>Use id “{rawUid}”</span>
                            <span className={cn('block text-xs', active ? 'text-indigo-100' : 'text-gray-500')}>Not in the cached user list</span>
                          </span>
                        </>
                      )}
                    </Combobox.Option>
                  )}
                  {results.map((user) => (
                    <Combobox.Option key={user.userId} value={user} className={({ active }) => cn('flex cursor-default select-none items-center gap-3 rounded-lg px-2.5 py-2', active ? 'bg-indigo-600 text-white' : 'text-gray-700')}>
                      {({ active }) => (
                        <>
                          <Avatar email={user.email} name={user.displayName || user.name} size="sm" />
                          <span className="min-w-0 flex-1">
                            <span className={cn('block truncate text-sm font-medium', active ? 'text-white' : 'text-gray-900')}>{user.email || user.userId}</span>
                            <span className={cn('block truncate font-mono text-2xs', active ? 'text-indigo-100' : 'text-gray-400')}>{user.userId}</span>
                          </span>
                          {user.lastSignInTime && <span className={cn('hidden text-xs sm:block', active ? 'text-indigo-100' : 'text-gray-400')}>{new Date(user.lastSignInTime).toLocaleDateString()}</span>}
                        </>
                      )}
                    </Combobox.Option>
                  ))}
                  {!loading && !rawUid && results.length === 0 && (
                    <li className="px-6 py-10 text-center text-sm text-gray-500">{error || 'No users match your search.'}</li>
                  )}
                </Combobox.Options>

                <div className="flex items-center gap-4 bg-gray-50 px-4 py-2.5 text-xs text-gray-500">
                  <span className="flex items-center gap-1"><Kbd>↑</Kbd><Kbd>↓</Kbd> navigate</span>
                  <span className="flex items-center gap-1"><Kbd>↵</Kbd> view as</span>
                  <span className="flex items-center gap-1"><Kbd>esc</Kbd> close</span>
                </div>
              </Combobox>
            </Dialog.Panel>
          </Transition.Child>
        </div>
      </Dialog>
    </Transition.Root>
  );
}
